import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
} from "three";
import { BloomEffect } from "./effects/BloomEffect";
import type {
  RendererBaseConfig,
  RendererShadowConfig,
  BloomEffectConfig,
} from "./type";
import {
  calculatePixelRatio,
  getShadowMapType,
  getOutputColorSpace,
  getDefaultRendererConfig,
  getDefaultBloomParams,
  getDefaultBlendParams
} from './util';

/**
 * 렌더러 설정에 따라 WebGL 렌더러 인스턴스를 생성하는 함수
 */
export function createWebGLRenderer(
  canvas: HTMLCanvasElement,
  config: RendererBaseConfig = {}
): WebGLRenderer {
  const finalConfig = { ...getDefaultRendererConfig(), ...config };

  const renderer = new WebGLRenderer({
    canvas,
    antialias: finalConfig.antialias,
    powerPreference: finalConfig.powerPreference,
    alpha: finalConfig.alpha,
    premultipliedAlpha: finalConfig.premultipliedAlpha,
    precision: finalConfig.precision,
    depth: finalConfig.depth,
    stencil: finalConfig.stencil,
    logarithmicDepthBuffer: finalConfig.logarithmicDepthBuffer
  });

  // 기본 설정 적용
  canvas.style.backgroundColor = "transparent";
  renderer.outputColorSpace = getOutputColorSpace(finalConfig.outputColorSpace);
  renderer.setPixelRatio(calculatePixelRatio(finalConfig.maxPixelRatio));

  // toneMappingExposure 설정
  if (finalConfig.toneMappingExposure !== undefined) {
    renderer.toneMappingExposure = finalConfig.toneMappingExposure;
  }

  // 섀도우 설정 적용
  applyShadowConfig(renderer, finalConfig.shadow);

  return renderer;
}

/**
 * 섀도우 설정을 렌더러에 적용하는 함수
 */
export function applyShadowConfig(
  renderer: WebGLRenderer,
  shadowConfig: RendererBaseConfig['shadow'] = {}
): void {
  const finalShadowConfig = { ...getDefaultRendererConfig().shadow, ...shadowConfig } as Required<RendererShadowConfig>;

  renderer.shadowMap.enabled = finalShadowConfig.enabled;
  renderer.shadowMap.type = getShadowMapType(finalShadowConfig.type);
  renderer.shadowMap.autoUpdate = finalShadowConfig.autoUpdate;
}

/**
 * 블룸 효과를 생성하는 함수
 */
export function createBloomEffect(
  scene: Scene,
  renderer: WebGLRenderer,
  camera: PerspectiveCamera,
  width: number,
  height: number,
  config: BloomEffectConfig = {}
): BloomEffect {
  const bloomParams = { ...getDefaultBloomParams(), ...config.bloomParams };
  const blendParams = { ...getDefaultBlendParams(), ...config.blendParams };

  const finalConfig: Required<BloomEffectConfig> = {
    bloomParams,
    blendParams
  };

  return new BloomEffect(scene, renderer, camera, width, height, finalConfig);
} 